import { useState, useRef, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = "Original",
  afterLabel = "IDK Avatar",
  className,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        // Invalidate cached rect on resize
        cachedRectRef.current = null;
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const computePosition = useCallback((clientX: number) => {
    const rect = cachedRectRef.current;
    if (!rect) return;
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(percent);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const container = containerRef.current;
    if (!container) return;
    // Cache rect once at the start of the drag — avoids forced reflow per move
    cachedRectRef.current = container.getBoundingClientRect();
    setIsDragging(true);
    computePosition(e.clientX);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [computePosition]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;
    const clientX = e.clientX;
    if (rafIdRef.current !== null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      computePosition(clientX);
    });
  }, [isDragging, computePosition]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    cachedRectRef.current = null;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full h-full select-none touch-none cursor-col-resize overflow-hidden", className)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* After (full) */}
      <img
        src={afterImage}
        alt="IDK Avatar"
        className="absolute inset-0 w-full h-full object-cover object-center"
        draggable={false}
        fetchPriority="high"
      />

      {/* Before (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${position}%` }}
      >
        <img
          src={beforeImage}
          alt="Original photo"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ width: containerWidth ? `${containerWidth}px` : '100%', maxWidth: 'none' }}
          draggable={false}
          fetchPriority="high"
        />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/80 z-10 pointer-events-none"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        {/* Handle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center pointer-events-none">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-foreground/70">
            <path d="M7 4L3 10L7 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13 4L17 10L13 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
        <div className="pill-badge py-1.5">
          <span className="text-xs font-medium text-muted-foreground">{beforeLabel}</span>
        </div>
      </div>
      <div className="absolute bottom-4 right-4 z-20 pointer-events-none">
        <div className="px-3 py-1.5 rounded-full bg-foreground text-background">
          <span className="text-xs font-semibold">{afterLabel}</span>
        </div>
      </div>
    </div>
  );
}